import React, { forwardRef, useContext, useEffect, useRef, useState } from "react";
import { ArrowContainer, Popover } from "react-tiny-popover";
import { ThemeContext } from "styled-components";
import { fontSizeMap as textFontSizeMap } from "../Text/Text.style.js";
import { defaultTheme, rem } from "../Theme/Theme.constants.js";
import { ITheme } from "../Theme/Theme.types.js";
import { DefaultTooltipProps, TooltipProps } from "./Tooltip.types.js";

const defaultProps: DefaultTooltipProps = {
    color: "dark",
    placement: ["top", "bottom", "right", "left"],
    delay: 0,
    elevation: defaultTheme.elevationLevels.max,
    maxWidth: rem(30),
};

// We need to inline all the styles of the content of the tooltip so they can be rendered across shadow DOM boundaries.
// If styles are not inlined, styled component will not apply the styles when the component will be rendered outside of the shadow DOM.
const getInlinedTooltipStyles = (theme: ITheme, color: "dark" | "light") => ({
    padding: `${theme.spacing["8"]} ${theme.spacing["12"]}`,
    backgroundColor: theme.grayscale[color === "dark" ? 90 : 10],
    color: theme.grayscale[color === "dark" ? 5 : 100],
    fontSize: theme.rem(textFontSizeMap["x-small"]),
    borderRadius: theme.radius.xs,
    boxShadow: theme.shadows.popover,
});

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(({ children, ...props }, ref): JSX.Element => {
    const { content, color, parentElement, placement, delay, elevation, visible, onOpenChange, containerStyle, maxWidth, className } = {
        ...defaultProps,
        ...props,
    };

    const [isOpen, setIsOpen] = useState(visible ?? false);
    const theme = useContext<ITheme>(ThemeContext);
    const enterTimeout = useRef<number>();

    const handleOpenChange = (newOpen: boolean) => {
        if (visible === undefined && newOpen !== isOpen) setIsOpen(newOpen);
        if (onOpenChange) onOpenChange(newOpen);
    };

    useEffect(() => {
        if (typeof visible === "boolean" && visible !== isOpen) {
            setIsOpen(visible);
        }
    }, [visible]);

    return (
        <Popover
            onClickOutside={() => {
                if (isOpen) handleOpenChange(false);
            }}
            ref={ref}
            parentElement={parentElement}
            isOpen={isOpen}
            content={({ position, childRect, popoverRect }) => {
                return color === "dark" ? (
                    <ArrowContainer
                        childRect={childRect}
                        popoverRect={popoverRect}
                        position={position}
                        arrowSize={7}
                        arrowColor={theme.grayscale[90]}
                    >
                        <div style={getInlinedTooltipStyles(theme, color)}>{content}</div>
                    </ArrowContainer>
                ) : (
                    <div style={getInlinedTooltipStyles(theme, color)}>{content}</div>
                );
            }}
            containerClassName={`popover-container${className ? ` ${className}` : ""}`}
            positions={placement}
            containerStyle={{
                ...(containerStyle as Record<string, string>),
                zIndex: `${elevation}`,
                maxWidth: typeof maxWidth === "number" ? theme.rem(maxWidth) : maxWidth,
            }}
        >
            {React.cloneElement(children, {
                onPointerEnter: (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                    if (children.props.onPointerEnter) children.props.onPointerEnter(e);
                    if (enterTimeout.current) clearTimeout(enterTimeout.current);
                    enterTimeout.current = window.setTimeout(() => {
                        handleOpenChange(true);
                    }, delay);
                },
                onPointerLeave: (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                    if (children.props.onPointerLeave) children.props.onPointerLeave(e);
                    if (enterTimeout.current) {
                        clearTimeout(enterTimeout.current);
                        enterTimeout.current = undefined;
                    }
                    handleOpenChange(false);
                },
                onMouseUp: (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                    if (children.props.onMouseUp) children.props.onMouseUp(e);
                    if (enterTimeout.current) {
                        clearTimeout(enterTimeout.current);
                        enterTimeout.current = undefined;
                    }
                    handleOpenChange(false);
                },
            })}
        </Popover>
    );
});
